/**
 * Bare handle + provider contact leaks (no @ / at / dot).
 * Shared by the client Text filter and server PII heuristics.
 * Never log the matched text.
 */

const EMAIL_PROVIDERS = 'gmail|yahoo|hotmail|outlook|icloud|aol|proton(?:mail)?';
const SOCIAL_PROVIDERS = 'insta|instagram|ig|snap(?:chat)?|telegram|whatsapp';
const PAYMENT_PROVIDERS = 'venmo|cash\\s*app|cashapp';

const PROVIDER_WORDS = new Set([
  'gmail',
  'yahoo',
  'hotmail',
  'outlook',
  'icloud',
  'aol',
  'proton',
  'protonmail',
  'insta',
  'instagram',
  'ig',
  'snap',
  'snapchat',
  'telegram',
  'whatsapp',
  'venmo',
  'cashapp',
  'cash',
  'app',
]);

const HANDLE_STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'do',
  'you',
  'use',
  'using',
  'used',
  'your',
  'my',
  'is',
  'on',
  'at',
  'to',
  'for',
  'and',
  'or',
  'with',
  'like',
  'have',
  'has',
  'was',
  'were',
  'this',
  'that',
  'what',
  'who',
  'how',
  'can',
  'just',
  'really',
  'about',
  'from',
  'not',
  'but',
  'if',
  'so',
  'we',
  'they',
  'i',
  'me',
  'it',
  'of',
  'in',
  'as',
  'be',
  'are',
  'any',
  'all',
  'some',
  'more',
  'also',
  'too',
  'than',
  'then',
  'now',
  'here',
  'there',
  'please',
  'thanks',
  'thank',
  'yes',
  'no',
  'ok',
  'okay',
  'hey',
  'hi',
  'hello',
  'still',
  'maybe',
  'because',
  'when',
  'where',
  'why',
  'which',
  'would',
  'could',
  'should',
  'will',
  'did',
  'does',
  'been',
  'being',
  'get',
  'got',
  'go',
  'com',
  'net',
  'org',
  'mail',
  'email',
  'dot',
  'our',
  'their',
  'them',
  'fine',
  'good',
  'okay',
  'sure',
  'yeah',
  'later',
  'soon',
  'today',
  'tonight',
  'tomorrow',
  'always',
  'never',
  'often',
  'usually',
  'sometimes',
  'better',
  'worse',
  'easier',
  'harder',
  'again',
  'something',
  'someone',
  'anyone',
  'everyone',
]);

const HANDLE_TOKEN = '[a-z0-9][a-z0-9._-]{2,29}';

/**
 * @param {string} [token]
 * @returns {boolean}
 */
export const isContactHandleToken = token => {
  const value = String(token || '')
    .toLowerCase()
    .replace(/[._-]+$/g, '');
  if (!value || value.length < 3) {
    return false;
  }
  if (HANDLE_STOPWORDS.has(value) || PROVIDER_WORDS.has(value)) {
    return false;
  }
  // "insta casually" is ordinary talk; real handles use digits, _ / ., or a username.
  if (/ly$/.test(value) && !/[\d._]/.test(value)) {
    return false;
  }
  return true;
};

const looksLikeEmailLocalPart = token => /[\d._]/.test(String(token || ''));

const firstMatchingHandle = (source, pattern, { requireLocalPart = false } = {}) => {
  const text = String(source || '');
  if (!text.trim()) {
    return null;
  }
  const regex = new RegExp(pattern, 'gi');
  let match = regex.exec(text);
  while (match) {
    const handle = match[1];
    if (isContactHandleToken(handle) && (!requireLocalPart || looksLikeEmailLocalPart(handle))) {
      return handle;
    }
    match = regex.exec(text);
  }
  return null;
};

const hasProviderHandle = (
  text,
  providers,
  { requireLocalPart = false, handleFirstNeedsLocalPart = false } = {}
) => {
  const source = String(text || '');
  if (!new RegExp(`\\b(?:${providers})\\b`, 'i').test(source)) {
    return false;
  }
  const patterns = [
    {
      pattern: `\\b(?:my\\s+)?(?:${providers})\\s+(?:is|:)\\s*@?(${HANDLE_TOKEN})\\b`,
      requireLocalPart,
    },
    {
      pattern: `\\b(${HANDLE_TOKEN})\\s+on\\s+(?:${providers})(?:\\s+com)?\\b`,
      requireLocalPart,
    },
    {
      pattern: `\\b(${HANDLE_TOKEN})\\s+(?:${providers})(?:\\s+com)?\\b`,
      requireLocalPart: requireLocalPart || handleFirstNeedsLocalPart,
    },
    {
      pattern: `\\b(?:${providers})\\s+@?(${HANDLE_TOKEN})\\b`,
      requireLocalPart,
    },
  ];
  return patterns.some(({ pattern, requireLocalPart: needsLocalPart }) =>
    firstMatchingHandle(source, pattern, { requireLocalPart: needsLocalPart })
  );
};

/**
 * samp1961 gmail / gmail samp1961 / my gmail is samp1961
 *
 * @param {string} [text]
 * @returns {boolean}
 */
export const hasBareEmailProviderHandle = text =>
  hasProviderHandle(text, EMAIL_PROVIDERS, { requireLocalPart: true });

/**
 * insta cooluser / ig samp1961 / snap qa_talker
 *
 * @param {string} [text]
 * @returns {boolean}
 */
export const hasBareSocialProviderHandle = text =>
  hasProviderHandle(text, 'insta|instagram|ig|snap(?:chat)?', {
    handleFirstNeedsLocalPart: true,
  }) || hasProviderHandle(text, 'telegram|whatsapp', { requireLocalPart: true });

/**
 * venmo qa_talker / cashapp samp1961
 *
 * @param {string} [text]
 * @returns {boolean}
 */
export const hasBarePaymentProviderHandle = text =>
  hasProviderHandle(text, PAYMENT_PROVIDERS, { requireLocalPart: true });
